<script setup>
import { router, useForm } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  roles: Array,
  permissionGroups: Object,
  permissions: Array,
  companies: Array,
  currentCompanyId: Number,
  isSystemAdmin: Boolean,
})

const { t } = useI18n()
const toast = useToast()
const confirm = useConfirm()

const selectedCompanyId = ref(props.currentCompanyId)
const isAddingRole = ref(false)
const showAddRoleDialog = ref(false)

const form = useForm({
  name: '',
  company_id: props.currentCompanyId,
})

// Flatten permissions from groups for the matrix
const groupedPermissions = computed(() => {
  const groups = []
  for (const [key, group] of Object.entries(props.permissionGroups)) {
    groups.push({
      key,
      label: group.label,
      permissions: group.permissions.map(permName => {
        const perm = props.permissions.find(p => p.name === permName)
        return {
          name: permName,
          label: perm?.label || permName,
        }
      }),
    })
  }
  return groups
})

const hasPermission = (role, permissionName) => {
  return role.permissions.includes(permissionName)
}

const togglePermission = (role, permissionName) => {
  const enabled = !hasPermission(role, permissionName)

  router.post(
    route('admin.role.toggle', role.id),
    {
      permission: permissionName,
      enabled,
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: enabled
            ? t('messages.admin.roles.permission_added')
            : t('messages.admin.roles.permission_removed'),
          life: 2000,
        })
      },
      onError: () => {
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.roles.toggle_error'),
          life: 3000,
        })
      },
    },
  )
}

const changeCompany = () => {
  router.get(
    route('admin.role.index'),
    { company_id: selectedCompanyId.value },
    { preserveState: true },
  )
}

const addRole = () => {
  if (!form.name.trim()) {
    return
  }

  isAddingRole.value = true
  form.company_id = selectedCompanyId.value

  form.post(route('admin.role.store'), {
    preserveScroll: true,
    onSuccess: () => {
      form.reset()
      showAddRoleDialog.value = false
      toast.add({
        severity: 'success',
        summary: t('messages.admin.roles.role_created'),
        life: 3000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.roles.create_error'),
        life: 3000,
      })
    },
    onFinish: () => {
      isAddingRole.value = false
    },
  })
}

const deleteRole = role => {
  confirm.require({
    message: t('messages.admin.roles.delete_confirm', {
      name: role.display_name || role.name,
    }),
    header: t('messages.admin.roles.delete_header'),
    accept: () => {
      router.delete(route('admin.role.destroy', role.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            summary: t('messages.admin.roles.role_deleted'),
            life: 3000,
          })
        },
        onError: () => {
          toast.add({
            severity: 'error',
            summary: t('messages.error'),
            detail: t('messages.admin.roles.delete_error'),
            life: 3000,
          })
        },
      })
    },
  })
}
</script>

<template>
  <div class="space-y-4 py-4">
    <!-- Header with company selector and add button -->
    <div class="flex items-center justify-between gap-4">
      <div class="flex items-center gap-4">
        <h2 class="text-xl font-semibold">{{ $t('messages.admin.roles.title') }}</h2>

        <!-- Company selector (system admins only) -->
        <Select
          v-if="isSystemAdmin && companies?.length > 0"
          v-model="selectedCompanyId"
          :options="companies"
          optionLabel="name"
          optionValue="id"
          :placeholder="$t('messages.admin.select.company')"
          class="w-64"
          @change="changeCompany"
        />
      </div>

      <Button @click="showAddRoleDialog = true" outlined>
        <i class="pi pi-plus mr-2"></i>
        {{ $t('messages.admin.roles.add_role') }}
      </Button>
    </div>

    <!-- Permission matrix -->
    <div class="overflow-x-auto rounded-lg border">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700">
              {{ $t('messages.admin.roles.permission') }}
            </th>
            <th
              v-for="role in roles"
              :key="role.id"
              class="min-w-[120px] px-4 py-3 text-center text-sm font-medium text-gray-700"
            >
              <div class="flex flex-col items-center gap-1">
                <div class="flex items-center gap-2">
                  <span>{{ role.display_name || role.name }}</span>
                  <i
                    v-if="role.is_system"
                    v-tooltip.top="$t('messages.admin.roles.system_role')"
                    class="pi pi-lock text-xs text-gray-400"
                  ></i>
                  <Button
                    v-if="!role.is_system"
                    text
                    severity="danger"
                    size="small"
                    @click="deleteRole(role)"
                  >
                    <i class="pi pi-trash text-xs"></i>
                  </Button>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 bg-white">
          <template v-for="group in groupedPermissions" :key="group.key">
            <!-- Group header row -->
            <tr class="bg-gray-100">
              <td
                :colspan="roles.length + 1"
                class="sticky left-0 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-gray-600"
              >
                {{ group.label }}
              </td>
            </tr>
            <!-- Permission rows -->
            <tr
              v-for="permission in group.permissions"
              :key="permission.name"
              class="hover:bg-gray-50"
            >
              <td class="sticky left-0 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <div class="flex items-center gap-2">
                  <span class="text-gray-400">├</span>
                  {{ permission.label }}
                </div>
              </td>
              <td
                v-for="role in roles"
                :key="`${role.id}-${permission.name}`"
                class="px-4 py-2 text-center"
              >
                <Checkbox
                  :modelValue="hasPermission(role, permission.name)"
                  :binary="true"
                  @click="togglePermission(role, permission.name)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Legend -->
    <div class="flex items-center gap-4 text-sm text-gray-500">
      <div class="flex items-center gap-2">
        <i class="pi pi-lock text-xs"></i>
        <span>{{ $t('messages.admin.roles.system_role_legend') }}</span>
      </div>
    </div>
  </div>

  <!-- Add Role Dialog -->
  <Dialog
    v-model:visible="showAddRoleDialog"
    :header="$t('messages.admin.roles.add_role')"
    modal
    class="w-full max-w-md"
  >
    <form @submit.prevent="addRole" class="space-y-4">
      <div>
        <label for="role-name" class="mb-2 block text-sm font-medium text-gray-700">
          {{ $t('messages.admin.roles.role_name') }}
        </label>
        <InputText
          id="role-name"
          v-model="form.name"
          :placeholder="$t('messages.admin.roles.role_name_placeholder')"
          class="w-full"
          autofocus
        />
        <small class="mt-1 block text-gray-500">
          {{ $t('messages.admin.roles.role_name_hint') }}
        </small>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          type="button"
          severity="secondary"
          outlined
          @click="showAddRoleDialog = false"
        >
          {{ $t('messages.close') }}
        </Button>
        <Button
          type="submit"
          :disabled="isAddingRole || !form.name.trim()"
          :loading="isAddingRole"
        >
          {{ $t('messages.admin.roles.add_role') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
